import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { TOOL_COLORS, TOOLS_BY_CATEGORY } from "@/lib/toolConstants";
import type { ToolRecommendation } from "@/components/prompt-builder/ToolRecommendationCards";

interface PromptModelPickerProps {
  recommendations?: ToolRecommendation[] | null;
  recommendationId?: string | null;
  scoreId?: string;
  originalPrompt: string;
  selectedTool: string | null;
  onOptimizedPrompt: (toolName: string, toolModel: string, optimizedPrompt: string) => void;
}

const RANK_LABELS = ["Best", "Better", "Good"];

function getToolModel(toolName: string, recommendations?: ToolRecommendation[] | null) {
  return recommendations?.find((rec) => rec.tool === toolName)?.model || "";
}

export function PromptModelPicker({
  recommendations,
  recommendationId,
  scoreId,
  originalPrompt,
  selectedTool,
  onOptimizedPrompt,
}: PromptModelPickerProps) {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isLoadingCache, setIsLoadingCache] = useState(Boolean(scoreId));
  const [displayTool, setDisplayTool] = useState(selectedTool || recommendations?.[0]?.tool || "Select model");
  const cachedVersions = useRef<Record<string, { prompt: string; model: string }>>({});

  const rankedToolNames = useMemo(
    () => new Set((recommendations || []).map((rec) => rec.tool)),
    [recommendations]
  );

  useEffect(() => {
    if (selectedTool) {
      setDisplayTool(selectedTool);
    }
  }, [selectedTool]);

  useEffect(() => {
    if (!scoreId) {
      setIsLoadingCache(false);
      return;
    }

    const loadCached = async () => {
      try {
        const { data } = await supabase
          .from("optimized_prompts")
          .select("tool_name, tool_model, optimized_prompt, created_at")
          .eq("score_id", scoreId)
          .order("created_at", { ascending: false });

        if (data && data.length > 0) {
          for (const row of data) {
            cachedVersions.current[row.tool_name] = {
              prompt: row.optimized_prompt,
              model: row.tool_model || "",
            };
          }

          if (!selectedTool) {
            const mostRecent = data[0];
            setDisplayTool(mostRecent.tool_name);
            onOptimizedPrompt(
              mostRecent.tool_name,
              mostRecent.tool_model || "",
              mostRecent.optimized_prompt
            );
          }
        }
      } catch (err) {
        console.error("Failed to load cached optimized prompts:", err);
      } finally {
        setIsLoadingCache(false);
      }
    };

    loadCached();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreId]);

  const persistSelectedTool = useCallback((toolName: string) => {
    if (!recommendationId) return;

    supabase
      .from("tool_recommendations")
      .update({ user_selected_tool: toolName })
      .eq("id", recommendationId)
      .then(({ error }) => {
        if (error) console.error("Failed to persist selected tool:", error);
      });
  }, [recommendationId]);

  const handleToolSelect = useCallback(async (toolName: string, toolModel = "") => {
    if (isOptimizing || !scoreId) return;

    const cached = cachedVersions.current[toolName];
    if (cached) {
      setDisplayTool(toolName);
      onOptimizedPrompt(toolName, cached.model || toolModel, cached.prompt);
      persistSelectedTool(toolName);
      return;
    }

    setIsOptimizing(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "optimize-prompt-for-tool-v6",
        {
          body: {
            final_prompt: originalPrompt,
            selected_tool: toolName,
            selected_model: toolModel,
            recommendation_id: recommendationId,
          },
        }
      );

      if (error || !data?.optimized_prompt) {
        toast.error("Failed to optimize prompt for this tool");
        return;
      }

      const optimizedText = data.optimized_prompt;
      cachedVersions.current[toolName] = { prompt: optimizedText, model: toolModel };

      supabase
        .from("optimized_prompts")
        .upsert(
          {
            score_id: scoreId,
            tool_name: toolName,
            tool_model: toolModel || null,
            optimized_prompt: optimizedText,
            recommendation_id: recommendationId,
          },
          { onConflict: "score_id,tool_name" }
        )
        .then(({ error: upsertErr }) => {
          if (upsertErr) console.error("Failed to cache optimized prompt:", upsertErr);
        });

      setDisplayTool(toolName);
      onOptimizedPrompt(toolName, toolModel, optimizedText);
      persistSelectedTool(toolName);
    } catch (err) {
      console.error("Optimization failed:", err);
      toast.error("Failed to optimize prompt for this tool");
    } finally {
      setIsOptimizing(false);
    }
  }, [
    isOptimizing,
    onOptimizedPrompt,
    originalPrompt,
    persistSelectedTool,
    recommendationId,
    scoreId,
  ]);

  const disabled = isLoadingCache || isOptimizing || !scoreId;
  const displayModel = getToolModel(displayTool, recommendations);

  return (
    <div className="flex items-center gap-3">
      <span className="hidden text-sm font-semibold text-muted-foreground md:inline">
        Optimized for:
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="h-10 min-w-[136px] max-w-[220px] justify-between rounded-xl border-2 border-success px-4 text-foreground hover:bg-success/10"
          >
            <span className="truncate">
              {isOptimizing ? "Optimizing..." : displayModel || displayTool}
            </span>
            {isOptimizing ? (
              <Loader2 className="ml-3 h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <ChevronDown className="ml-3 h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[420px] w-[320px] overflow-y-auto">
          {recommendations && recommendations.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Recommended
              </DropdownMenuLabel>
              {recommendations.map((rec, index) => (
                <DropdownMenuItem
                  key={`${rec.rank}-${rec.tool}`}
                  className={cn("items-start gap-3 py-2", TOOL_COLORS[rec.tool])}
                  onSelect={() => handleToolSelect(rec.tool, rec.model || "")}
                >
                  <span className="mt-0.5 rounded-full bg-primary/20 px-2 py-0.5 text-[11px] font-semibold text-primary">
                    {RANK_LABELS[index] || `#${rec.rank}`}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 font-medium text-foreground">
                      {rec.tool}
                      {selectedTool === rec.tool && <Check className="h-3.5 w-3.5 text-success" />}
                    </span>
                    {rec.model && (
                      <span className="block text-xs text-muted-foreground">{rec.model}</span>
                    )}
                  </span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
            All tools
          </DropdownMenuLabel>
          {Object.entries(TOOLS_BY_CATEGORY).map(([category, tools]) => {
            const availableTools = tools.filter((tool) => !rankedToolNames.has(tool));
            if (availableTools.length === 0) return null;

            return (
              <div key={category}>
                <DropdownMenuLabel className="px-2 py-1 text-[11px] font-medium text-muted-foreground">
                  {category}
                </DropdownMenuLabel>
                {availableTools.map((tool) => (
                  <DropdownMenuItem
                    key={tool}
                    className={cn("justify-between", TOOL_COLORS[tool])}
                    onSelect={() => handleToolSelect(tool)}
                  >
                    <span>{tool}</span>
                    {selectedTool === tool && <Check className="h-3.5 w-3.5 text-success" />}
                  </DropdownMenuItem>
                ))}
              </div>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {isOptimizing && <Sparkles className="hidden h-4 w-4 text-primary md:block" />}
    </div>
  );
}
